// Core lib dependencies.
import axios from 'axios';
import { polyfill } from 'es6-promise';
polyfill();

/**
 * <pre>
 * Ajax service.
 *
 * Summary of responsibilities:
 *
 *  - provides abstraction layer for ajax calls.
 *  - configures each request with common params.
 * </pre>
 *
 * @author Various
 * @namespace Common.Services.AjaxService
 */
const AjaxService = (function() {
  'use strict';

  // Axios client.
  var client;

  // Configuration service;
  var _configService;

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Init.
   *
   * @memberOf Common.Services.AjaxService
   *
   * @param configService {Common.Services.ConfigService} The config service.
   */
  const init = configService => {
    // Store injected params.
    _configService = configService;

    // Create a new Axios client.
    client = axios.create({
      // Allow response codes up to 400
      validateStatus: function(status) {
        return status >= 200 && status <= 400;
      },
    });
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Return common params for all requests.
   *
   * @memberOf Common.Services.AjaxService
   *
   * @return {object} Common params object.
   */
  const getCommonParams = () => {
    const commonParams = {};

    // Append api key.
    if (_configService.get('keys.api')) {
      commonParams.apikey = _configService.get('keys.api');
    }

    // Append stage mode and ptk param if required.
    if (_configService.get('stage.mode')) {
      commonParams.stageMode = _configService.get('stage.mode');
      commonParams.ptk = _configService.get('stage.token');
    }

    // Cache buster.
    commonParams.nocache = new Date().getTime();

    return commonParams;
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Execute request.
   *
   * @see {@link https://github.com/mzabriskie/axios#request-config|Axios Documentation}
   *
   * @memberOf Common.Services.AjaxService
   *
   * @param options {object} Axios configuration object.
   * @return {object} Axios response.
   */
  const executeRequest = options => {
    // Get common params.
    const commonParams = getCommonParams();

    // Combine with params unique to this request.
    const mergedParams = Object.assign(commonParams, options.params);

    // Assign them back to the options object.
    options.params = mergedParams;

    // Return the result of the request.
    return client
      .request(options)
      .then(sucessHandler)
      .catch(errorHandler);
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Internal success response handler.
   *
   */
  const sucessHandler = response => {
    return response;
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Internal error response handler.
   *
   */
  const errorHandler = error => {
    return error;
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Api Methods.

  /**
   * Execute GET request.
   *
   * @see {@link https://github.com/mzabriskie/axios#request-config|Axios Documentation}
   *
   * @memberOf Common.Services.AjaxService
   *
   * @param url {string} URL for request.
   * @param params {object} Params to append to the request query string.
   * @return {object} Axios response.
   */
  const get = (url, params) => {
    // Define the options.
    const options = {
      url:    url,
      method: 'get',
      params: params,
    };

    return executeRequest(options);
  };

  /**
   * Execute POST request.
   *
   * @see {@link https://github.com/mzabriskie/axios#request-config|Axios Documentation}
   *
   * @memberOf Common.Services.AjaxService
   *
   * @param url {string} URL for request.
   * @param params {object} Params to append to the request query string.
   * @param data {object} Data to append to the request body.
   * @return {object} Axios response.
   */
  const post = (url, params, data) => {
    // Define the options.
    const options = {
      url:    url,
      method: 'post',
      params: params,
      data:   data,
    };

    return executeRequest(options);
  };

  // Expose scoped properties.
  return {
    init: init,

    get:  get,
    post: post,
  };
})();

export default AjaxService;
